// ════════════════════════════════════════════════════════════════════════════
// GuidedDemo — an in-product, presenter-style walkthrough that builds a complete
// governed pipeline LIVE, in the real product, driven by the Cortex assistant
// chat. A fixed slide panel with a clickable agenda index and Back / Next / Auto
// controls. Each slide drives the real UI + chat and WAITS until it has VERIFIED
// the real result (storages, strategies, target rows) before advancing.
//
// NOTHING about the data is predetermined: the USER names the project (a guided
// step) and uploads WHATEVER file(s) they want. Sources are named after the
// uploaded files and the destination table is DERIVED from the first file — no
// domain, file count, or theme is hardcoded. The pipeline lands in a dedicated
// dev demo PostgreSQL (target-pg-demo; credentials live server-side in the
// assistant via add_demo_postgres_target — never in the client).
//
// Flow: name the project → create + switch into it (in place, no page reload) →
// upload your data → the assistant maps + builds the DAG → run → real rows land.
//
// Build steps go through the assistant CHAT (recipe mutations are cortex MCP
// tools, not REST routes). We open the chat and drive it via postMessage:
// `chat-send {text}` to send, and we listen for `chat-turn-start`/`chat-turn-end`
// so we never collide with an in-flight turn (the headless agent rejects that).
// ════════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createProject } from "../services/projects";
import { listStorages, listStrategies, runPipeline, readRecords } from "../services/pipeline";
import { SourceFieldMappingView } from "@/entities/all";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../contexts/ProjectContext";
import { Play, Pause, ChevronRight, ChevronLeft, X, CheckCircle2, Loader2, Sparkles, Upload, Database } from "lucide-react";

const SS_KEY = "cortex_guided_demo";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Derive a safe sql-ish identifier from a filename (drop extension + path + the
// timestamp/partition noise). e.g. "Q1 Sales 2025.csv" → "q1_sales_2025".
function ident(name, fallback) {
  const base = String(name || "").split("/").pop().replace(/\.[a-z0-9]+$/i, "");
  const s = base.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 50);
  return s || fallback;
}

// ── chat driver ───────────────────────────────────────────────────────────────
function chatIframe() { return document.querySelector('iframe[src*="chat.html"]'); }
function postToChat(msg) {
  const f = chatIframe();
  if (f && f.contentWindow) { f.contentWindow.postMessage(msg, "*"); return true; }
  return false;
}
function openChat() {
  // Unified Cortex chat (orchestrator harness; delegates to governance).
  window.dispatchEvent(new CustomEvent("cortex:open-harness", { detail: { harness: "orchestrator" } }));
}

// Slugify a user-typed project name into a valid cortex slug (lowercase, starts
// with a letter, letters/digits/underscores only).
function slugify(name) {
  let s = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 63);
  if (s && !/^[a-z]/.test(s)) s = "p_" + s;
  return s;
}

export default function GuidedDemo({ onClose, navigate }) {
  const { user, switchProject } = useAuth();
  const { projectSlug: activeProject } = useProject();

  // The user names the project in step 1 — nothing is predetermined. switchProject()
  // updates the auth context in place (no page reload), so the overlay stays
  // mounted for the whole walkthrough — no resume/persist needed.
  const [projName, setProjName] = useState("");
  const SLUG = slugify(projName);
  const NAME = projName.trim() || SLUG;
  const slugValid = /^[a-z][a-z0-9_]{0,62}$/.test(SLUG);

  const [step, setStep] = useState(0);
  const [autoplay, setAutoplay] = useState(false);
  const [status, setStatus] = useState("");
  // Broadcast a global data-changed event so whatever step page is shown re-fetches
  // and reflects live progress (sources, mappings, elements, strategies) as the
  // chat builds them — the pages otherwise only load once on mount.
  const refreshPages = useCallback(() => {
    try { window.dispatchEvent(new CustomEvent("cortex:data-changed")); } catch {}
  }, []);
  // Navigate the main app to a route so the user watches the new project's pages
  // change behind the panel. Prefer react-router; fall back to history+popstate.
  const goPage = useCallback((path) => {
    try {
      if (navigate) { navigate(path); return; }
    } catch {}
    try { window.history.pushState({}, "", path); window.dispatchEvent(new PopStateEvent("popstate")); } catch {}
  }, [navigate]);

  // While the overlay is mounted, tick a data-changed broadcast every few seconds
  // so the visible step page keeps refreshing during long-running chat builds.
  useEffect(() => {
    const t = setInterval(() => { try { window.dispatchEvent(new CustomEvent("cortex:data-changed")); } catch {} }, 4000);
    return () => clearInterval(t);
  }, []);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  // Uploaded sources discovered at the upload step: [{ name, table }]. Persisted
  // (the File objects themselves can't survive a reload, but the upload happens
  // AFTER the project-switch reload, so that's fine).
  const [sources, setSources] = useState([]);
  const [table, setTable] = useState("");
  const fileInputRef = useRef(null);

  // Step 3 input mode: upload your own files, OR connect an external database as a
  // source. DB mode collects a kind-agnostic connection config and registers it
  // via add_source (the engine's cortex.source.* dispatcher — inherited from the
  // core-dev prototype — handles any kind by `kind`). No per-project connector
  // authoring is needed: a new fork already inherits every existing source adapter.
  const [sourceMode, setSourceMode] = useState("file"); // "file" | "db"
  const [db, setDb] = useState({ kind: "postgres", source_name: "", host: "", port: "5432", database: "", user: "", password: "", schema: "public", table: "" });
  const setDbField = (k, v) => setDb((d) => ({ ...d, [k]: v }));

  // turn-idle tracking
  const busyRef = useRef(false);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === "chat-turn-start") busyRef.current = true;
      if (e.data?.type === "chat-turn-end") busyRef.current = false;
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // No reload happens during the demo, so nothing needs persisting.
  const persist = useCallback(() => {}, []);
  const clearPersist = useCallback(() => {}, []);

  const waitChatIdle = useCallback(async (capMs = 240_000) => {
    const t0 = Date.now();
    await sleep(800);
    while (Date.now() - t0 < capMs) { if (!busyRef.current) return true; await sleep(1500); }
    return false;
  }, []);
  // Wait until the chat iframe exists AND its websocket is connected (status-bar
  // shows .connected). chat.html is same-origin so we can read its DOM. Posting
  // chat-send before the WS is open queues the message but can race the session
  // handshake, so we gate on connection first.
  const waitChatConnected = useCallback(async (capMs = 60_000) => {
    const t0 = Date.now();
    while (Date.now() - t0 < capMs) {
      const f = chatIframe();
      try {
        const doc = f && f.contentDocument;
        if (doc && doc.getElementById("status-bar")?.classList.contains("connected")) return true;
      } catch { /* not ready */ }
      await sleep(800);
    }
    return false;
  }, []);
  const chatSend = useCallback(async (text) => {
    openChat();
    for (let i = 0; i < 40 && !chatIframe(); i++) await sleep(500);
    await waitChatConnected();   // ensure the WS is up before sending
    await waitChatIdle();
    postToChat({ type: "chat-send", text });
    await sleep(1800);           // let turn_start flip busy
  }, [waitChatIdle, waitChatConnected]);
  const chatStep = useCallback(async (text, check, { totalMs = 360_000, statusFn } = {}) => {
    if (await check()) return true;
    await chatSend(text);
    const t0 = Date.now();
    let sends = 1;
    while (Date.now() - t0 < totalMs) {
      await sleep(5000);
      if (statusFn) setStatus(await statusFn(Math.round((Date.now() - t0) / 1000)));
      if (await check()) return true;
      if (!busyRef.current && sends < 4) {
        if (await check()) return true;
        postToChat({ type: "chat-send", text: text + " (please proceed — call the tools directly now)" });
        await sleep(1500); sends++;
      }
    }
    return await check();
  }, [chatSend]);

  // ── ground-truth readers ────────────────────────────────────────────────────
  // The read APIs return { storages: [...] } / { strategies: [...] } — unwrap to
  // the array (tolerate a bare array too).
  const storages = useCallback(async () => { try { const r = await listStorages(); return Array.isArray(r) ? r : (r?.storages || []); } catch { return []; } }, []);
  const strategies = useCallback(async () => { try { const r = await listStrategies(); return Array.isArray(r) ? r : (r?.strategies || []); } catch { return []; } }, []);
  const countFileSources = useCallback(async () => (await storages()).filter((s) => (s.storage_type || s.type) === "file").length, [storages]);
  // Count SOURCE storages of any kind (file OR external DB), excluding the
  // pipeline's target storages. A DB source registered via add_source is a
  // non-file, non-target storage, so the file-only count misses it.
  const countSources = useCallback(async () => (await storages()).filter((s) => {
    const id = s.storage_id || "";
    const t = (s.storage_type || s.type || "").toLowerCase();
    if (t === "file") return true;                       // uploaded file source
    if (/^(parquet_raw|parquet_processed)/.test(id)) return false; // engine intermediates
    if (/^(postgres|mysql|mongo):\d/.test(id)) return false;       // pipeline targets
    return !!t;                                          // any other registered source (db kinds)
  }).length, [storages]);
  const countStrategies = useCallback(async () => (await strategies()).length, [strategies]);
  // Ground-truth count of source field mappings (the view the Source Mappings page
  // reads) — used to detect when the dictionary+mapping step has actually produced
  // rows, so the overlay advances on real state instead of a fixed timeout.
  const countMappings = useCallback(async () => {
    try { const r = await SourceFieldMappingView.listWithTotal("source_field_name", 1, 0); return r?.total ?? (Array.isArray(r?.data) ? r.data.length : 0); }
    catch { return 0; }
  }, []);
  const postgresTargetId = useCallback(async () => {
    const st = await storages();
    const tgt = st.find((s) => /^(postgres|mysql)/.test(s.storage_id || ""));
    return tgt ? tgt.storage_id.split(":")[1] : null;
  }, [storages]);
  const targetRowCount = useCallback(async () => {
    const tid = await postgresTargetId();
    if (!tid) return 0;
    try { const r = await readRecords("data_target", tid, { limit: 1 }); return r?.total ?? (Array.isArray(r?.rows) ? r.rows.length : 0); }
    catch { return 0; }
  }, [postgresTargetId]);

  // ── upload: signed S3 PUT (same path as the chat attach), then chat-ingest ────
  const token = () => localStorage.getItem("access_token") || "";
  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setRunning(true);
    const uploaded = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setStatus(`uploading ${i + 1}/${files.length}: ${f.name}…`);
        // X-Cortex-Project is required — without it the backend falls back to
        // the JWT's stale `active_project` claim and signs against the wrong
        // project (or 400s when the claim is unset). See fetchSignedPut in
        // public/chat.html.
        const r = await fetch("/api/chat/upload/sign", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token()}`,
            "X-Cortex-Project": activeProject,
            "X-Cortex-Branch": "main",
          },
          body: JSON.stringify({ filename: f.name, content_type: f.type || "application/octet-stream" }),
        });
        if (!r.ok) {
          let msg = `HTTP ${r.status}`;
          try { const j = await r.json(); msg = j.message || j.error || msg; } catch {}
          throw new Error(msg);
        }
        const signed = await r.json();
        const put = await fetch(signed.url, { method: "PUT", headers: { "Content-Type": f.type || "application/octet-stream" }, body: f });
        if (!put.ok) throw new Error(`upload HTTP ${put.status}`);
        uploaded.push({ name: ident(f.name, `source_${i + 1}`), s3_uri: signed.s3_uri, filename: f.name });
      }
      const tbl = ident(files[0].name, "records");
      const srcMeta = uploaded.map((u) => ({ name: u.name, table: tbl }));
      setSources(srcMeta); setTable(tbl);
      persist({ sources: srcMeta, table: tbl, step });
      // Ask the assistant to ingest each uploaded file as a source.
      setStatus("ingesting uploaded files as data points…");
      const msg =
        `Ingest these uploaded files as data sources by calling ingest_uploaded_file for EACH: ` +
        uploaded.map((u) => `ingest_uploaded_file { s3_uri: "${u.s3_uri}", source_name: "${u.name}" }`).join(" ; ") +
        `. Call the tool for every one now; don't ask anything.`;
      await chatStep(msg, async () => (await countFileSources()) >= 1, {
        totalMs: 240_000,
        statusFn: async (s) => `ingesting + profiling… (${await countFileSources()} sources, ${s}s)`,
      });
      setStatus(`✓ ${uploaded.length} file(s) uploaded + registered → table "${tbl}"`);
    } catch (e) {
      setStatus(`⚠ upload failed: ${(e?.message || e).toString().slice(0, 80)}`);
    } finally {
      setRunning(false);
    }
  }, [chatStep, countFileSources, persist, step]);

  // Register an EXTERNAL DATABASE as a data source (any kind). Drives the chat to
  // call add_source { source_name, kind, config } then analyze_source so the
  // schema is captured — exactly like a file source, but reading a live DB. The
  // engine's cortex.source.* dispatcher (inherited from the prototype) handles the
  // kind; no per-project connector authoring is needed.
  const registerDbSource = useCallback(async () => {
    const name = ident(db.source_name || db.table || db.database || db.kind, "db_source");
    if (!db.host || !db.database) { setStatus("⚠ enter at least host + database ↓"); return; }
    setRunning(true);
    try {
      const tbl = ident(db.table || db.database || name, "records");
      // Kind-specific config; omit empty fields. password is sent through the chat
      // tool call (server-side recipe config), same as the manual add_source path.
      const cfg = {
        host: db.host, database: db.database,
        ...(db.port ? { port: Number(db.port) } : {}),
        ...(db.user ? { user: db.user } : {}),
        ...(db.password ? { password: db.password } : {}),
        ...(db.schema ? { schema: db.schema } : {}),
        ...(db.table ? { table: db.table } : {}),
      };
      setSources([{ name, table: tbl }]); setTable(tbl);
      setStatus(`registering ${db.kind} source "${name}"…`);
      const msg =
        `Register an EXTERNAL DATABASE as a data source in this project: call ` +
        `add_source { source_name: "${name}", kind: "${db.kind}", config: ${JSON.stringify(cfg)} }, ` +
        `then call analyze_source for it so its schema is captured. ` +
        `Do this now and do NOT ask for confirmation — call the tools directly and report the registered source + discovered columns when done.`;
      await chatStep(msg, async () => (await countSources()) >= 1, {
        totalMs: 240_000,
        statusFn: async (s) => { refreshPages(); return `connecting + profiling ${db.kind}… (${await countSources()} sources, ${s}s)`; },
      });
      refreshPages();
      setStatus(`✓ ${db.kind} source "${name}" connected → table "${tbl}"`);
    } catch (e) {
      setStatus(`⚠ connect failed: ${(e?.message || e).toString().slice(0, 80)}`);
    } finally {
      setRunning(false);
    }
  }, [db, chatStep, countSources, refreshPages]);

  // ── slides ────────────────────────────────────────────────────────────────────
  const srcNames = sources.map((s) => s.name).join(", ") || "your files";
  const STEPS = [
    {
      title: "Build a governed pipeline — from scratch",
      lines: [
        "Cortex models a pipeline as a typed transformation DAG over data points.",
        "You'll name a new project, upload your own data, and build it live —",
        "from raw files to a queryable relational target. Press Next to begin.",
      ],
      run: async () => { setStatus("Ready. Press Next or Auto to begin."); },
    },
    {
      title: "1 · Name your project",
      lines: [
        "Give this project a name. We'll create a versioned Dolt recipe DB and a",
        "paired Flow engine fork under this slug.",
      ],
      run: async () => {
        setStatus(slugValid ? `✓ slug: ${SLUG}` : "Type a project name below ↓");
      },
      body: () => (
        <div className="mt-2">
          <input
            autoFocus
            value={projName}
            onChange={(e) => { setProjName(e.target.value); }}
            placeholder="e.g. Acme Sales"
            className="w-full h-8 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active"
          />
          <div className="mt-1 text-xs text-page-muted">
            {projName ? <>slug: <code>{SLUG || "—"}</code>{!slugValid && projName ? " (invalid — needs a letter to start)" : ""}</> : "lowercase slug derived automatically"}
          </div>
        </div>
      ),
    },
    {
      title: "2 · Create the project",
      lines: [
        "Cortex provisions a versioned Dolt recipe DB + a paired Flow engine fork,",
        "then switches you into it — all live, no page reload.",
      ],
      run: async () => {
        if (!slugValid) { setStatus("⚠ go back and enter a project name first"); return; }
        if (activeProject !== SLUG) {
          setStatus("creating the project (Dolt DB + Flow fork)…");
          try { await createProject({ name: NAME, slug: SLUG, description: "Built with the Cortex guided walkthrough" }); } catch (e) { /* may already exist */ }
          // The project is selected by the URL (single source of truth). Navigate
          // into the new project; the walkthrough continues there.
          setStatus("opening the project…");
          if (typeof navigate === "function") {
            navigate(`/p/${encodeURIComponent(SLUG)}/b/main/dashboard`);
          } else {
            window.location.assign(`/p/${encodeURIComponent(SLUG)}/b/main/dashboard`);
          }
          return;
        }
        // CRITICAL: the chat's WS session is bound to the project at connect time.
        // Open the chat now (post-switch) and force it to reconnect a fresh session
        // pointed at the NEW project — otherwise every chat tool call would run
        // against the previously-active project.
        openChat();
        for (let i = 0; i < 20 && !chatIframe(); i++) await sleep(500);
        postToChat({ type: "chat-reconnect", activeProject: SLUG, activeBranch: "main", token: localStorage.getItem("access_token") || "" });
        await sleep(1500);
        setStatus(`✓ project ${SLUG} created + active`);
      },
    },
    {
      title: "3 · Bring your data",
      lines: [
        "Upload your own files (CSV / XLSX / JSON / Parquet), <b>or</b> connect an external database of any kind as a live source.",
        "Cortex registers it as a <b>data point</b> and discovers its schema; the destination table is named after your file or table.",
      ],
      run: async () => {
        goPage("/data-sources");  // show the project's Sources page
        if (sources.length) { setStatus(`✓ ${sources.length} source(s) registered → table "${table}"`); return; }
        setStatus(sourceMode === "db" ? "Enter your database connection below, then “Connect + discover schema” ↓" : "Click “Choose files” to upload your data ↓");
      },
      body: () => (
        <div className="mt-2">
          {/* mode toggle: upload files OR connect an external database */}
          <div className="flex items-center gap-1 mb-2 p-0.5 rounded bg-page-bg w-fit">
            <button onClick={() => setSourceMode("file")}
              className={`flex items-center gap-1 h-7 px-2.5 rounded text-xs ${sourceMode === "file" ? "bg-card-bg shadow font-medium" : "text-page-muted"}`}>
              <Upload size={13} /> Upload files
            </button>
            <button onClick={() => setSourceMode("db")}
              className={`flex items-center gap-1 h-7 px-2.5 rounded text-xs ${sourceMode === "db" ? "bg-card-bg shadow font-medium" : "text-page-muted"}`}>
              <Database size={13} /> Connect a database
            </button>
          </div>

          {sourceMode === "file" ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={running}
              className="flex items-center gap-2 h-8 px-3 rounded bg-nav-active text-white text-xs disabled:opacity-50"
            >
              <Upload size={14} /> {sources.length ? "Upload more" : "Choose files"}
            </button>
          ) : (
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-1.5">
                <input value={db.kind} onChange={(e) => setDbField("kind", e.target.value)} placeholder="kind (postgres, mysql, mongo, …)"
                  className="flex-1 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
                <input value={db.source_name} onChange={(e) => setDbField("source_name", e.target.value)} placeholder="source name (optional)"
                  className="flex-1 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
              </div>
              <div className="flex gap-1.5">
                <input value={db.host} onChange={(e) => setDbField("host", e.target.value)} placeholder="host"
                  className="flex-[2] h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
                <input value={db.port} onChange={(e) => setDbField("port", e.target.value)} placeholder="port"
                  className="w-16 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
              </div>
              <div className="flex gap-1.5">
                <input value={db.database} onChange={(e) => setDbField("database", e.target.value)} placeholder="database"
                  className="flex-1 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
                <input value={db.schema} onChange={(e) => setDbField("schema", e.target.value)} placeholder="schema"
                  className="flex-1 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
              </div>
              <div className="flex gap-1.5">
                <input value={db.user} onChange={(e) => setDbField("user", e.target.value)} placeholder="user"
                  className="flex-1 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
                <input value={db.password} onChange={(e) => setDbField("password", e.target.value)} type="password" placeholder="password"
                  className="flex-1 h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
              </div>
              <input value={db.table} onChange={(e) => setDbField("table", e.target.value)} placeholder="table or view (optional)"
                className="h-7 px-2 rounded border border-page-divider bg-page-bg text-xs outline-none focus:border-nav-active" />
              <button onClick={registerDbSource} disabled={running}
                className="flex items-center gap-2 h-8 px-3 rounded bg-nav-active text-white text-xs disabled:opacity-50 w-fit">
                <Database size={14} /> {sources.length ? "Reconnect" : "Connect + discover schema"}
              </button>
            </div>
          )}
          {sources.length > 0 && (
            <div className="mt-1.5 text-xs text-page-muted">{sources.length} source(s): {srcNames}</div>
          )}
        </div>
      ),
    },
    {
      title: "4 · Map columns to a canonical dictionary",
      lines: [
        "The assistant inspects your uploaded schema, proposes canonical <b>data elements</b>,",
        "and LLM-maps every source column into them.",
      ],
      run: async () => {
        goPage("/source-mappings");  // show mappings filling in live
        const ok = await chatStep(
          `For the sources in this project (${srcNames}): infer a sensible set of canonical data elements from their columns and create them with create_data_element (give each a clear canonical_name; mark the natural key as the primary key). Then call create_source_field_mappings for each source. ` +
          `Do this end-to-end now and do NOT ask for confirmation — call the tools directly and only report the final element + mapping counts when done.`,
          async () => (await countMappings()) >= 1,
          { totalMs: 360_000, statusFn: async (s) => { refreshPages(); return `building dictionary + mapping… (${await countMappings()} mappings, ${s}s)`; } },
        ).catch(() => false);
        refreshPages();
        setStatus(ok ? `✓ elements created + columns mapped (${await countMappings()})` : "⚠ mapping incomplete — Next to retry");
      },
    },
    {
      title: "5 · Declare the destination (PostgreSQL)",
      lines: [`Add the demo Postgres target and bind every element to <code>${table || "your table"}.&lt;field&gt;</code>.`],
      run: async () => {
        goPage("/data-dictionary");  // show elements + target bindings
        await chatStep(
          `Call add_demo_postgres_target { target_name: "demo_pg", table: "${table || "records"}" } now, then bind every data element in this project to the demo_pg target, table "${table || "records"}" (target_column = the element's canonical_name) by calling bind_element_to_target for each element — don't skip any. ` +
          `Do all of this in one go and do NOT ask for confirmation — call the tools directly; only report when the target and all bindings are done.`,
          async () => !!(await postgresTargetId()),
          { totalMs: 240_000, statusFn: async (s) => { refreshPages(); return `adding target + binding elements… (${s}s)`; } },
        ).catch(() => {});
        await waitChatIdle(240_000);
        refreshPages();
        setStatus("✓ Postgres target + element bindings declared");
      },
    },
    {
      title: "6 · Wire the transformation DAG",
      lines: ["A standard cleanup rule, then build the DAG:", "per source <b>file → parquet_raw → parquet_processed → postgres</b>."],
      run: async () => {
        goPage("/pipeline");  // watch the DAG appear as it's wired
        const ok = await chatStep(
          `Call attach_default_rules now (one call, no arguments). It deterministically ensures EVERY mapped data element has a processing rule attached — a normalize_number rule for numeric columns (so "$1,924.56" becomes 1924.56) and trim_whitespace for the rest — which is required for the pyarrow processor to emit each column (an unruled column is dropped). ` +
          `Then call build_pipeline_dag, then query_strategies. Do NOT ask for confirmation — call the tools directly and report every strategy (kind, domain → codomain) when done.`,
          async () => (await countStrategies()) >= 3,
          { totalMs: 240_000, statusFn: async (s) => { refreshPages(); return `wiring the DAG… (${await countStrategies()} transformations, ${s}s)`; } },
        );
        setStatus(ok ? `✓ DAG wired — ${await countStrategies()} transformations` : "⚠ DAG incomplete — Next to retry");
      },
    },
    {
      title: "7 · The pipeline, as a typed DAG",
      lines: ["Open the Pipeline page — every data point a node, every transformation an arrow.", "Your source chains converge on the Postgres target."],
      run: async () => { goPage("/pipeline"); setStatus(`✓ DAG shown for ${SLUG}`); },
    },
    {
      title: "8 · Run the pipeline",
      lines: ["Run the pipeline — Cortex topo-sorts the DAG and executes each", "transformation, streaming records into PostgreSQL."],
      run: async () => {
        setStatus("running the pipeline end-to-end…");
        try { await runPipeline(); } catch (e) { setStatus(`⚠ run error: ${(e?.message || e).toString().slice(0, 80)}`); }
        let rows = 0;
        for (let i = 0; i < 90; i++) {
          await sleep(4000);
          rows = await targetRowCount();
          setStatus(`running… (${rows} rows in postgres ${table || ""}, ${i * 4}s)`);
          if (rows > 0) break;
        }
        setStatus(rows > 0 ? `✓ pipeline ran — ${rows} rows in postgres "${table}"` : "⚠ run submitted; check the runs panel");
      },
    },
    {
      title: "Done — a governed pipeline, built live",
      lines: [
        "Name → your data → schema → dictionary → target → DAG → run.",
        "Versioned in Dolt, executed by the Flow engine, <b>no hand-written code</b>.",
        "Built in the real product, on your own project — every step verified.",
      ],
      run: async () => { setStatus("✓ complete"); setDone(true); clearPersist(); },
    },
  ];

  const N = STEPS.length;
  const cur = STEPS[Math.min(step, N - 1)];

  const runStep = useCallback(async () => {
    if (running) return;
    setRunning(true);
    try { await cur.run(); }
    catch (e) { setStatus(`⚠ ${(e?.message || e).toString().slice(0, 90)}`); }
    finally { setRunning(false); }
  }, [cur, running]);

  // NB: the chat is opened in step 1 AFTER the project switch (and reconnected to
  // the new project), so we deliberately do NOT open it on mount — opening early
  // would bind the chat session to the previously-active project.

  // Run a step's action exactly once whenever `step` changes (including the
  // initial mount / resume-after-reload). Uses a ref so re-renders don't re-run.
  const lastRunStep = useRef(-1);
  useEffect(() => {
    if (lastRunStep.current === step) return;
    lastRunStep.current = step;
    const t = setTimeout(() => { runStep(); }, step === 0 ? 300 : 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Autoplay: advance after a step settles. The name step (1) waits for a valid
  // slug; the upload step (3) waits for the human to pick files; never while running.
  useEffect(() => {
    persist({});
    if (!autoplay || running || done) return;
    if (step >= N - 1) return;
    if (step === 1 && !slugValid) return; // wait for the user to name the project
    if (step === 3 && sources.length === 0) return; // wait for upload
    const t = setTimeout(() => setStep((s) => Math.min(N - 1, s + 1)), 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, running, autoplay, done, sources.length, slugValid]);

  const go = (k) => { if (k >= 0 && k < N) { lastRunStep.current = -1; setStep(k); } };

  return (
    <div className="fixed left-5 bottom-5 z-[60] w-[340px] max-w-[calc(100vw-2.5rem)] rounded-[10px] bg-card-bg shadow-2xl border border-page-divider text-page-text overflow-hidden select-none">
      {/* Always-rendered upload input (referenced by the step-2 button AND by
          the e2e verifier via setInputFiles) — kept out of the conditional step
          body so it's reliably present. */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv,.xlsx,.xls,.parquet,.json"
        className="hidden"
        onChange={(e) => uploadFiles(e.target.files)}
      />
      <div className="flex items-center justify-between px-4 py-2.5 bg-nav-active text-white">
        <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles size={15} /> Cortex Guided</div>
        <button onClick={() => { clearPersist(); onClose?.(); }} className="p-1 rounded hover:bg-white/20" title="Close guide"><X size={16} /></button>
      </div>

      <div className="px-3 py-2 border-b border-page-divider max-h-[200px] overflow-y-auto">
        {STEPS.map((s, i) => {
          const label = s.title.replace(/^\s*\d+\s*·\s*/, "");
          const isCur = i === step;
          const isDoneRow = i < step || (done && i === N - 1);
          return (
            <button key={i} onClick={() => go(i)}
              className={`w-full flex items-center gap-2 text-left text-xs px-2 py-1 rounded ${isCur ? "bg-page-bg font-semibold" : "hover:bg-page-bg/60"}`}>
              <span className="shrink-0 w-4 text-center">
                {isDoneRow ? <CheckCircle2 size={13} className="text-green-600 inline" /> : isCur ? <ChevronRight size={13} className="inline" /> : <span className="text-page-muted">{i}</span>}
              </span>
              <span className={isCur ? "" : "text-page-muted"}>{label}</span>
            </button>
          );
        })}
      </div>

      <div className="px-4 py-3">
        <div className="text-sm font-semibold mb-1">{cur.title}</div>
        <div className="text-xs text-page-muted space-y-1">
          {cur.lines.map((l, i) => <div key={i} dangerouslySetInnerHTML={{ __html: l }} />)}
        </div>
        {cur.body && cur.body()}
        <div className="mt-2 text-xs flex items-center gap-1.5 min-h-[18px]">
          {running && <Loader2 size={12} className="animate-spin shrink-0" />}
          <span className={status.startsWith("⚠") ? "text-amber-600" : status.startsWith("✓") ? "text-green-600" : "text-page-text"}>{status}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-page-divider">
        <button onClick={() => go(step - 1)} disabled={step === 0 || running} className="flex items-center gap-1 h-8 px-3 rounded text-xs hover:bg-page-bg disabled:opacity-40"><ChevronLeft size={14} /> Back</button>
        <button onClick={() => go(step + 1)} disabled={step >= N - 1 || running} className="flex items-center gap-1 h-8 px-3 rounded bg-nav-active text-white text-xs disabled:opacity-40">Next <ChevronRight size={14} /></button>
        <button onClick={() => setAutoplay((a) => !a)} className="flex items-center gap-1 h-8 px-3 rounded text-xs hover:bg-page-bg ml-auto">
          {autoplay ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Auto</>}
        </button>
      </div>
    </div>
  );
}
